/**
 * FirmStammdatenForm — the shared edit form for the company master data (D1-14 + P2-8).
 *
 * ONE form, TWO hosts: the inline edit inside the Firmenstammdaten card and the "Bearbeiten"
 * dialog in the Kunde detail header both render this, so the two surfaces can never drift on
 * which fields are editable, how they are labelled, or how a draft becomes a PUT body.
 *
 * The draft keeps every field as a plain string ('' for empty). `draftToPatch` sends the full
 * editable set to PUT /firms/:id and lets the SERVER decide what actually changed (only a field
 * whose value differs from the stored one is written and marked as broker-overridden), so an
 * accidental no-op save never locks a field against enrichment.
 *
 * P2-8 (spec-part2-stefan-delta.md §3.2, module p2-customer-stammdaten-web) adds the customer
 * Stammdaten expansion, split into the two groups the spec defines:
 *   - AUTO-DERIVED (A + M-override): noga_code, branche, founding_year, business_activity,
 *     vat_number, mwst_pflichtig — the register/enrichment fills these, but a broker edit here
 *     is `hold()`-guarded server-side (EnrichmentWorker) so it survives the next re-enrichment.
 *   - PURE-MANUAL (M): annual_revenue, customer_since, reference_currency, input_tax_deductible,
 *     address_supplement — never written by enrichment, always broker-entered.
 * Both groups round-trip through PUT /firms/:id via the SAME draft→patch mechanism as the base
 * fields (the server, not the client, decides what actually changed) — no new wiring needed.
 * `mwst_pflichtig` / `input_tax_deductible` are TRI-STATE booleans (true/false/unknown), which a
 * plain checkbox cannot express, so the draft carries them as '' | 'true' | 'false' like every
 * other field and a `<select>` (mirrors `legal_form`) offers the third "unbekannt" state.
 *
 * [Source: apps/web/src/features/administration/components.tsx] (TextInput)
 * [Source: apps/api/src/modules/firms/update-firm.constants.ts] (editable fields, legal forms)
 * [Source: apps/api/src/modules/firms/dto/update-firm.dto.ts] (P2-7 UpdateFirmDto — field types/limits)
 * [Source: packages/shared/src/noga.ts] (resolveBranche — live NOGA→Branche preview)
 */

import { resolveBranche } from '@brokly/shared';
import { TextInput } from '../../administration/components';
import { FirmNameAutocomplete } from '../../../components/ui/FirmNameAutocomplete';
import type { FirmDetailWire, FirmMasterDataPatch } from '../../../lib/api-client';

/** The legal_form enum, as options for the select (matches the DB enum + card labels). */
export const LEGAL_FORM_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '(keine Angabe)' },
  { value: 'ag', label: 'Aktiengesellschaft (AG)' },
  { value: 'gmbh', label: 'GmbH' },
  { value: 'eg', label: 'Genossenschaft' },
  { value: 'einzelfirma', label: 'Einzelfirma' },
  { value: 'verein', label: 'Verein' },
  { value: 'stiftung', label: 'Stiftung' },
  { value: 'other', label: 'Andere' },
];

/** Tri-state boolean options (mwst_pflichtig / input_tax_deductible) — matches the draft's '' | 'true' | 'false'. */
const TRI_STATE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '(unbekannt)' },
  { value: 'true', label: 'Ja' },
  { value: 'false', label: 'Nein' },
];

/** Shared className for the native `<select>` inputs (mirrors TextInput's look). */
const SELECT_CLASS =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Shared className for the multi-line `<textarea>` (business_activity). */
const TEXTAREA_CLASS =
  'min-h-[4.5rem] w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** A draft of the editable Firmenstammdaten — every field a string, '' meaning empty. */
export interface FirmDraft {
  name: string;
  legal_form: string;
  uid: string;
  address: string;
  postcode: string;
  city: string;
  canton: string;
  phone: string;
  // ── P2-8 auto-derived group (A + M-override; hold()-guarded in EnrichmentWorker) ──────────
  noga_code: string;
  branche: string;
  /** Integer as a string ('' = unset). */
  founding_year: string;
  business_activity: string;
  vat_number: string;
  /** Tri-state boolean as a string: '' = unknown, 'true' / 'false'. */
  mwst_pflichtig: string;
  // ── P2-8 pure-manual group (M; never written by enrichment) ───────────────────────────────
  annual_revenue: string;
  /** ISO date (yyyy-MM-dd), '' = unset. */
  customer_since: string;
  reference_currency: string;
  /** Tri-state boolean as a string: '' = unknown, 'true' / 'false'. */
  input_tax_deductible: string;
  address_supplement: string;
}

/** Tri-state boolean (`null` = unknown/unset) <-> the plain-string draft representation. */
function boolToDraft(v: boolean | null | undefined): string {
  if (v === true) return 'true';
  if (v === false) return 'false';
  return '';
}

/** The inverse of `boolToDraft`: '' -> null (unknown), else the literal boolean. */
function draftToBool(v: string): boolean | null {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
}

/** Build an editable draft from the read shape (nulls → ''). */
export function draftFromWire(d: FirmDetailWire): FirmDraft {
  return {
    name: d.name ?? '',
    legal_form: d.legal_form ?? '',
    uid: d.uid ?? '',
    address: d.address ?? '',
    postcode: d.postcode ?? '',
    city: d.city ?? '',
    canton: d.canton ?? '',
    phone: d.phone ?? '',
    noga_code: d.noga_code ?? '',
    branche: d.branche ?? '',
    founding_year: d.founding_year != null ? String(d.founding_year) : '',
    business_activity: d.business_activity ?? '',
    vat_number: d.vat_number ?? '',
    mwst_pflichtig: boolToDraft(d.mwst_pflichtig),
    annual_revenue: d.annual_revenue ?? '',
    customer_since: d.customer_since ?? '',
    // NOT NULL DEFAULT 'CHF' at the DB — the wire always carries a value, but a defensive
    // fallback keeps the form usable even against a partial test fixture.
    reference_currency: d.reference_currency ?? 'CHF',
    input_tax_deductible: boolToDraft(d.input_tax_deductible),
    address_supplement: d.address_supplement ?? '',
  };
}

/**
 * The PUT body for a draft: the full editable set (the server marks only what truly changed).
 * String fields are trimmed; '' is sent through and coerced to null server-side for nullable
 * string columns. `founding_year` becomes a number (or null when blank); `mwst_pflichtig` /
 * `input_tax_deductible` become a real boolean or null (never the string 'true'/'false').
 */
export function draftToPatch(draft: FirmDraft): FirmMasterDataPatch {
  const foundingYear = draft.founding_year.trim();
  return {
    name: draft.name.trim(),
    legal_form: draft.legal_form.trim(),
    uid: draft.uid.trim(),
    address: draft.address.trim(),
    postcode: draft.postcode.trim(),
    city: draft.city.trim(),
    canton: draft.canton.trim().toUpperCase(),
    phone: draft.phone.trim(),
    noga_code: draft.noga_code.trim(),
    branche: draft.branche.trim(),
    founding_year: foundingYear === '' ? null : Number(foundingYear),
    business_activity: draft.business_activity.trim(),
    vat_number: draft.vat_number.trim(),
    mwst_pflichtig: draftToBool(draft.mwst_pflichtig),
    annual_revenue: draft.annual_revenue.trim(),
    customer_since: draft.customer_since.trim(),
    reference_currency: draft.reference_currency.trim().toUpperCase(),
    input_tax_deductible: draftToBool(draft.input_tax_deductible),
    address_supplement: draft.address_supplement.trim(),
  };
}

/** True when nothing in the draft differs from the original (a no-op save). */
export function draftUnchanged(a: FirmDraft, b: FirmDraft): boolean {
  return (Object.keys(a) as (keyof FirmDraft)[]).every(
    (k) => a[k].trim() === b[k].trim(),
  );
}

/**
 * True when `reference_currency` is not a plausible ISO 4217 code (exactly 3 letters). The DB
 * column is `char(3) NOT NULL` — unlike every other field here, an empty/short value is not a
 * valid "clear", so the host components gate Save on this the same way they gate on `nameEmpty`.
 */
export function referenceCurrencyInvalid(draft: FirmDraft): boolean {
  return !/^[A-Za-z]{3}$/.test(draft.reference_currency.trim());
}

/**
 * True when `annual_revenue` is set but not a plausible `numeric(14,2)` decimal (max 12 integer
 * digits, 2 decimals). Mirrors the server DTO's `NUMERIC_14_2_RE` exactly so a bad value ("1'000",
 * "12,50", "abc") is caught before the round-trip instead of coming back as a 400/500. Empty is a
 * valid "clear" (the column is nullable), so — unlike `reference_currency` — '' is NOT invalid.
 */
export function annualRevenueInvalid(draft: FirmDraft): boolean {
  const v = draft.annual_revenue.trim();
  return v !== '' && !/^-?\d{1,12}(\.\d{1,2})?$/.test(v);
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

/**
 * The labelled inputs. Presentational: it owns no state — the host holds the draft and passes
 * `draft` + `onField`. `disabled` greys everything out while a save is in flight.
 */
export function FirmStammdatenForm({
  draft,
  onField,
  disabled,
}: {
  draft: FirmDraft;
  onField: (field: keyof FirmDraft, value: string) => void;
  disabled?: boolean;
}) {
  return (
    <>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field label="Firmenname">
          <FirmNameAutocomplete
            value={draft.name}
            disabled={disabled}
            onChange={(name) => onField('name', name)}
            onPick={(row) => {
              onField('name', row.name);
              if (row.uid) onField('uid', row.uid);
            }}
            aria-label="Firmenname"
          />
        </Field>
      </div>

      <Field label="Rechtsform">
        <select
          value={draft.legal_form}
          disabled={disabled}
          onChange={(e) => onField('legal_form', e.target.value)}
          aria-label="Rechtsform"
          className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {LEGAL_FORM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="UID">
        <TextInput
          value={draft.uid}
          disabled={disabled}
          placeholder="CHE-123.456.789"
          onChange={(e) => onField('uid', e.target.value)}
          aria-label="UID"
        />
      </Field>

      <div className="sm:col-span-2">
        <Field label="Adresse">
          <TextInput
            value={draft.address}
            disabled={disabled}
            onChange={(e) => onField('address', e.target.value)}
            aria-label="Adresse"
          />
        </Field>
      </div>

      <Field label="PLZ">
        <TextInput
          value={draft.postcode}
          disabled={disabled}
          onChange={(e) => onField('postcode', e.target.value)}
          aria-label="PLZ"
        />
      </Field>

      <Field label="Ort">
        <TextInput
          value={draft.city}
          disabled={disabled}
          onChange={(e) => onField('city', e.target.value)}
          aria-label="Ort"
        />
      </Field>

      <Field label="Kanton">
        <TextInput
          value={draft.canton}
          disabled={disabled}
          maxLength={2}
          placeholder="z. B. BS"
          onChange={(e) => onField('canton', e.target.value)}
          aria-label="Kanton"
        />
      </Field>

      <Field label="Telefon">
        <TextInput
          value={draft.phone}
          disabled={disabled}
          onChange={(e) => onField('phone', e.target.value)}
          aria-label="Telefon"
        />
      </Field>
    </div>

    {/* ── P2-8 auto-derived group: the register fills these; a broker edit here survives the
        next re-enrichment (hold()-guarded server-side). The live NOGA→Branche preview under
        the NOGA-Code field is a hint only — it never auto-writes the Branche field, so an
        existing manual Branche override is never silently clobbered. ── */}
    <fieldset className="mt-4 border-0 p-0">
      <legend className="mb-2 text-xs font-semibold text-muted-foreground">
        Automatisch ermittelt <span className="font-normal">(vom Handelsregister, überschreibbar)</span>
      </legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="NOGA-Code">
          <>
            <TextInput
              value={draft.noga_code}
              disabled={disabled}
              placeholder="z. B. 62010"
              onChange={(e) => onField('noga_code', e.target.value)}
              aria-label="NOGA-Code"
            />
            {draft.noga_code.trim() !== '' && resolveBranche(draft.noga_code) !== null && (
              <span className="text-xs text-muted-foreground">
                → {resolveBranche(draft.noga_code)}
              </span>
            )}
          </>
        </Field>

        <Field label="Branche">
          <TextInput
            value={draft.branche}
            disabled={disabled}
            onChange={(e) => onField('branche', e.target.value)}
            aria-label="Branche"
          />
        </Field>

        <Field label="Gründungsjahr">
          <TextInput
            type="number"
            value={draft.founding_year}
            disabled={disabled}
            min={1800}
            max={2100}
            placeholder="z. B. 2015"
            onChange={(e) => onField('founding_year', e.target.value)}
            aria-label="Gründungsjahr"
          />
        </Field>

        <Field label="MwSt-pflichtig">
          <select
            value={draft.mwst_pflichtig}
            disabled={disabled}
            onChange={(e) => onField('mwst_pflichtig', e.target.value)}
            aria-label="MwSt-pflichtig"
            className={SELECT_CLASS}
          >
            {TRI_STATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="MwSt-Nummer">
          <TextInput
            value={draft.vat_number}
            disabled={disabled}
            placeholder="CHE-123.456.789 MWST"
            onChange={(e) => onField('vat_number', e.target.value)}
            aria-label="MwSt-Nummer"
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label="Geschäftstätigkeit">
            <textarea
              value={draft.business_activity}
              disabled={disabled}
              onChange={(e) => onField('business_activity', e.target.value)}
              aria-label="Geschäftstätigkeit"
              className={TEXTAREA_CLASS}
            />
          </Field>
        </div>
      </div>
    </fieldset>

    {/* ── P2-8 pure-manual group: never written by enrichment, always broker-entered. ── */}
    <fieldset className="mt-4 border-0 p-0">
      <legend className="mb-2 text-xs font-semibold text-muted-foreground">Manuell erfasst</legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Jahresumsatz">
          <>
            <TextInput
              value={draft.annual_revenue}
              disabled={disabled}
              inputMode="decimal"
              placeholder="z. B. 250000.00"
              onChange={(e) => onField('annual_revenue', e.target.value)}
              aria-label="Jahresumsatz"
            />
            {annualRevenueInvalid(draft) && (
              <span className="text-xs text-destructive">
                Bitte eine Zahl eingeben (z. B. 250000.00), ohne Tausendertrennzeichen.
              </span>
            )}
          </>
        </Field>

        <Field label="Referenzwährung">
          <TextInput
            value={draft.reference_currency}
            disabled={disabled}
            maxLength={3}
            placeholder="CHF"
            onChange={(e) => onField('reference_currency', e.target.value)}
            aria-label="Referenzwährung"
          />
        </Field>

        <Field label="Kunde seit">
          <TextInput
            type="date"
            value={draft.customer_since}
            disabled={disabled}
            onChange={(e) => onField('customer_since', e.target.value)}
            aria-label="Kunde seit"
          />
        </Field>

        <Field label="Vorsteuerabzugsberechtigt">
          <select
            value={draft.input_tax_deductible}
            disabled={disabled}
            onChange={(e) => onField('input_tax_deductible', e.target.value)}
            aria-label="Vorsteuerabzugsberechtigt"
            className={SELECT_CLASS}
          >
            {TRI_STATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="sm:col-span-2">
          <Field label="Adresszusatz">
            <TextInput
              value={draft.address_supplement}
              disabled={disabled}
              placeholder="z. B. c/o, Stockwerk"
              onChange={(e) => onField('address_supplement', e.target.value)}
              aria-label="Adresszusatz"
            />
          </Field>
        </div>
      </div>
    </fieldset>
    </>
  );
}
